import { ecId, ecLean, record, type LogRow, type Row } from '../ability/context';
import { readItemField } from '../compile/item-field-reader';
import { readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers';
import { fromBaseClone, fromBaseUuid } from '../compile/from-base-context';
import { phpIsNumeric } from '../inventory/lift-occupancy';
import { appendSpawnStatus, mergeSpawnPlans, emptySpawnPlan, type SpawnReaders, type SpawnRowPlan } from '../spawn/row-plan';
import { stackTarget } from '../inventory/move-rules';
import { statusRows, statusItemId } from '../statuses/expiry';
import { phpJsonInt, phpJsonString } from '../compile/php-json-values';
import { trim } from '../php-compat/strings';

type BitReaders = Pick<SpawnReaders, 'entries' | 'value' | 'item' | 'inventory' | 'uuid' | 'clock'>;
const INTEGRATED_OBJECT = '36C061BF-E480-4334-961C-26500150E3C4';

/** apply_death_effect's monster_bit rows: structured qty floors/clamps; scalar uuid|qty keeps its quantity. */
export function monsterBitDrops(target: Row, read: Pick<BitReaders, 'entries'>): Array<{ itemId: string; qty: number }> {
    return read.entries(target, 'monster_bit').flatMap(entry => {
        const row = record(entry);
        if (row) {
            const id = readItemField(row, 'item_id') ?? readItemField(row, 'item_layer_effect');
            if (typeof id !== 'string' || !fromBaseUuid(trim(id))) return [];
            const quantity = readItemField(row, 'qty');
            return [{ itemId: trim(id), qty: phpIsNumeric(quantity) ? Math.max(1, Math.floor(Number(quantity))) : 1 }];
        }
        if (typeof entry !== 'string' && typeof entry !== 'number' && typeof entry !== 'boolean') return [];
        const [itemId, qty = 1] = trim(phpJsonString(entry)).split('|');
        if (!itemId) return [];
        if (!phpIsNumeric(qty)) throw new Error('death:monster-bit-quantity-invalid');
        return [{ itemId, qty: phpJsonInt(qty) }];
    });
}

/** Each harvested part starts on the dead creature with its own Integrated Object status and log. */
export function planMonsterBits(target: Row, vanished: boolean, read: BitReaders, integratedId = INTEGRATED_OBJECT): { plan: SpawnRowPlan; rows: LogRow[] } {
    const targetId = ecId(target), rows: LogRow[] = [];
    let plan = emptySpawnPlan();
    if (vanished || readItemField(target, 'item_type') === 'player_character' || ecLean(target, 'item_type') === 'player_character'
        || !fromBaseUuid(targetId)) return { plan, rows };
    const holder = fromBaseClone(target), carried = readInventoryRows(holder, 'inventory');
    holder.inventory = carried;
    for (const drop of monsterBitDrops(target, read)) {
        const item = read.item(drop.itemId), addition = read.inventory(item, holder, drop.qty, '', true);
        const parts = addition.writes.filter(write => write.table === 'inventory' && write.op === 'insert'
            && readItemField(write.row, 'possess_id') === targetId);
        if (parts.length !== 1) throw new Error('death:monster-bit-factory-row-contract');
        const part = parts[0]!, source: Row = { ...item, inventory: part.row };
        const stack = Number(read.value(item, 'spoilage_int') ?? 0) > 0 ? { target: null } : stackTarget(source,
            carried.filter(row => readInventoryField(row, 'possess_id') === targetId),
            { isFloor: false, x: 0, y: 0, z: 0 }, 0, { value: (row, key) => row ? read.value(row, key) : null,
                inventoryField: (row, key) => readInventoryField(row ?? null, key),
                itemType: row => String(readItemField(row ?? null, 'item_type') ?? ''), statusItemIds: row => statusRows(row, 'target').map(statusItemId) });
        if ('unknown' in stack) throw new Error(`death:monster-bit-stack:${stack.unknown}`);
        let inventoryId = String(readItemField(part.row, 'inventory_id'));
        let inventory: Row = source;
        if (stack.target) {
            inventory = carried.find(row => readInventoryField(row, 'inventory_id') === stack.target)!;
            inventoryId = String(readInventoryField(inventory, 'inventory_id'));
            const before = Number(readInventoryField(inventory, 'item_qty') ?? 1), next = before + drop.qty;
            const pending = plan.writes.find(write => ['death_loot', 'inventory'].includes(write.table) && readItemField(write.row, 'inventory_id') === inventoryId);
            if (pending) pending.row.item_qty = next;
            else plan.writes.push({ table: 'inventory', op: 'update', row: { inventory_id: inventoryId, item_qty: next }, before: { item_qty: before } });
            (inventory.inventory as Row).item_qty = next;
            // A non-spoiling monster bit has no initialization statuses/contents to retain after its merge.
        } else {
            part.table = 'death_loot';
            carried.push(inventory);
            plan = mergeSpawnPlans(plan, addition);
        }
        if (statusRows(inventory, 'target').some(status => statusItemId(status) === integratedId)) continue;
        const statusPlan = emptySpawnPlan();
        appendSpawnStatus(statusPlan, read, inventoryId, inventoryId, integratedId);
        if (stack.target && !plan.writes.some(write => write.op === 'insert' && readItemField(write.row, 'inventory_id') === inventoryId))
            statusPlan.writes[0]!.table = 'death_part_statuses';
        plan = mergeSpawnPlans(plan, statusPlan);
        inventory.statuses = { ...record(inventory.statuses), target: [...statusRows(inventory, 'target'), { status_item_id: integratedId }] };
        rows.push({ roll_type_1: 'status_effect_applied', roll_type_2: null, roll_item_id: integratedId,
            roll_by: inventoryId, roll_to: inventoryId, roll_result: 0 });
    }
    return { plan, rows };
}
